import { Autocomplete, TextField, FormHelperText, Typography } from "@mui/material"
import { Supplier } from "../types/api"
import _ from "lodash"
import { useEffect, useState } from "react"
import { getSuppliers } from "src/services/supplier.service"

// The one supplier selector. Works inside a react-hook-form (pass register,
// setValue, trigger and errors, as the product and purchase order forms do)
// or standalone in a dialog by omitting them.
interface SupplierPickerProps {
    value: string | null | undefined
    supplierChanged: (event: any, value: Supplier | null) => void
    register?: (name: string, options: any) => {}
    errors?: any
    setValue?: (name: string, value: any) => void
    trigger?: (name: string) => void
    label?: string
    autoFocus?: boolean
    size?: "small" | "medium"
}
export const SupplierPicker = (props:SupplierPickerProps) => {
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
    const { setValue, trigger } = props;

    useEffect(() => {
        getSuppliers({}, setSuppliers, () => {}, () => {});
      }, []);

    useEffect(() => {
        if (props.value && suppliers.length > 0) {
            const supplier = suppliers.find(s => s.guid === props.value);
            setSelectedSupplier(supplier || null);
            // Initialize form validation for existing supplier
            if (supplier && setValue && trigger) {
                setValue("supplier", supplier.guid);
                trigger("supplier");
            }
        } else {
            setSelectedSupplier(null);
        }
    }, [props.value, suppliers, setValue, trigger]);

    const handleSupplierChange = (event: any, newValue: Supplier | null) => {
        setSelectedSupplier(newValue);
        props.supplierChanged(event, newValue);
        // Update form validation
        if (setValue && trigger) {
            setValue("supplier", newValue ? newValue.guid : "");
            trigger("supplier");
        }
    };

    // Archived suppliers are not offered, but one already set on the record
    // being edited stays visible (labelled) so an old order never shows blank.
    const options = suppliers.filter((s) => !s.archivedAt || s.guid === props.value);
    const optionLabel = (s: Supplier) => (s.archivedAt ? `${s.name} (archived)` : s.name);

    const formProps = props.register
        ? props.register("supplier", {
            required: "You must select a supplier from the list",
            validate: (value) =>
              selectedSupplier !== null || (value && value !== "") || "You must select a supplier from the list"
          })
        : {};
    const error = props.errors?.supplier;

    return (
        <>
          {_.isEmpty(suppliers) && (
            <Typography>Loading suppliers...</Typography>
          )}
          {!_.isEmpty(suppliers) && (
            <Autocomplete
              options={options}
              getOptionLabel={optionLabel}
              isOptionEqualToValue={(option, value) => option.guid === value.guid}
              value={selectedSupplier}
              onChange={handleSupplierChange}
              size={props.size}
              renderInput={(params) => (
                <TextField
                  {...params}
                  {...formProps}
                  label={props.label || "Supplier *"}
                  error={!!error}
                  autoFocus={props.autoFocus}
                  sx={{ textAlign: "left" }}
                />
              )}
              fullWidth
            />
          )}
          {error && (
            <FormHelperText error>
              {error.message || "You must select a Supplier from the list."}
            </FormHelperText>
          )}
        </>
    )
}
